import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  TextField,
  Grid,
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/system";
import formatDate from "../../../../utils/formatDate";
import { useParams } from "react-router-dom";
import styles from "./RestaurantDetails.module.css";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Visibility from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import {
  useLoaderContext,
  useToastContext,
} from "../../../../Context/ToastContext";
import ViewDocument from "../../ViewDocument/ViewDocument";
import { useUserContext } from "../../../../Context/UserContext";
import { Helmet } from "react-helmet";
import { apiGet, apiPut } from "../../../../utils/ApiManager";

const getFinancialDetails = process.env.REACT_APP_GET_FINANC_DETAILS;
const getUpdateFinancialDetails =
  process.env.REACT_APP_HANDLE_SAVE_FINANC_DETAILS;

// Custom styled Paper for form sections
const SectionPaper = styled(Paper)({
  padding: "20px",
  marginBottom: "20px",
  // backgroundColor: '#f5f5f5',
});

const RestaurantDetails = ({ next, prev }) => {
  const { restaurantID } = useParams();
  const [formData, setFormData] = useState({
    restaurantID: restaurantID,
    fssaiLicenseNo: "",
    fssaiIssuedOn: "",
    fssaiValidUpto: "",
    fssaiDocument: null,
    fssaiFile: null,
    gstRegistrationNo: "",
    gstIssuedOn: "",
    cgst: "",
    sgst: "",
    gstDocument: null,
    gstFile: null,
    // panType: "",
    panCardNo: "",
    panDocument: null,
    panFile: null,
    // bankAccountType: "",
    bankAccountNo: "",
    bankName: "",
    bankIfscCode: "",
    bankDocument: null,
    bankFile: null,
    upiId: "",
    commission: "",
    partialPercentage: "",
    partialPerPerson: "",
    minBookingPerPerson: "",
    landlordNOCDocument: null,
    landlordNOCFile: null,
    landlordNOCIssuedOn: "",
    landlordNOCValidTill: "",
    landlordNOCComment: "",
    electricityNOCDocument: null,
    electricityNOCFile: null,
    electricityNOCIssuedOn: "",
    electricityNOCValidTill: "",
    electricityNOCComment: "",
    fireNOCDocument: null,
    fireNOCFile: null,
    fireNOCIssuedOn: "",
    fireNOCValidTill: "",
    fireNOCComment: "",
    policeNOCDocument: null,
    policeNOCFile: null,
    policeNOCIssuedOn: "",
    policeNOCValidTill: "",
    policeNOCComment: "",
    eatingHouseLicenseDocument: null,
    eatingHouseLicenseFile: null,
    eatingHouseLicenseIssuedOn: "",
    eatingHouseLicenseValidTill: "",
    eatingHouseLicenseComment: "",
    leaseAgreementDocument: null,
    leaseAgreementFile: null,
    leaseAgreementIssuedOn: "",
    leaseAgreementValidTill: "",
    leaseAgreementComment: "",
  });
  const [showDocument, setShowDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const { showToast } = useToastContext(); // Destructure toast values from context
  const { setLoading } = useLoaderContext();
  const { requestConfirmation, getToken } = useUserContext();
  const fssaiFileInputRef = useRef(null);
  const gstFileInputRef = useRef(null);
  const panFileInputRef = useRef(null);
  const bankFileInputRef = useRef(null);
  const nocFileInputRef = useRef({});
  const licenseFileInputRef = useRef({});

  useEffect(() => {
    const nocNames = [
      "landlordNOCDocument",
      "electricityNOCDocument",
      "fireNOCDocument",
      "policeNOCDocument",
    ];
    nocNames.forEach((name) => {
      if (!nocFileInputRef.current[name]) {
        nocFileInputRef.current[name] = React.createRef();
      }
    });

    const licenseNames = [
      "eatingHouseLicenseDocument",
      "leaseAgreementDocument",
    ];
    licenseNames.forEach((name) => {
      if (!licenseFileInputRef.current[name]) {
        licenseFileInputRef.current[name] = React.createRef();
      }
    });
  }, []);

  const handleClick = (inputRef) => {
    if (inputRef?.current) {
      inputRef.current.value = null;
    }
  };

  const handleViewDocument = (docUrl) => {
    setDocumentUrl(docUrl);
    setShowDocument(true);
  };

  const handleCloseDocument = () => {
    setDocumentUrl("");
    setShowDocument(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value, // Update formData with the new input value
    }));

    // Clear any errors associated with this field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDateChange = (newValue, section) => {
    if (newValue) {
      const date = dayjs(newValue);

      // Check if the date is valid
      if (!date.isValid()) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [section]: "Invalid date format", // Show error if date is invalid
        }));
      }
      // Check if the date is in the future for 'fssaiIssuedOn' or 'gstIssuedOn'
      else if (
        (section === "fssaiIssuedOn" || section === "gstIssuedOn") &&
        date.isAfter(dayjs(), "day")
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [section]: "Date cannot be in the future", // Error for future 'fssaiIssuedOn' and 'gstIssuedOn'
        }));
      } else {
        // If valid, store the date as an ISO string and clear any errors
        setFormData((prevData) => ({
          ...prevData,
          [section]: date.toISOString(),
        }));
        setErrors((prevErrors) => ({
          ...prevErrors,
          [section]: "", // Clear the error
        }));
      }
    } else {
      // Handle case when the date is cleared
      setErrors((prevErrors) => ({
        ...prevErrors,
        [section]: "This field is required", // Error for empty date
      }));
      setFormData((prevData) => ({
        ...prevData,
        [section]: "", // Clear the date
      }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
  
    if (!file) return;
  
    const fileSizeInKB = file.size / 1024;
  
    if (fileSizeInKB > 300) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "File size must be less than 300 KB.",
      }));
      return;
    }
  
    // Check if the file is a PDF
    if (file.type !== "application/pdf") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: "Only PDF files are allowed.",
      }));
      return;
    }
  
    // Update the form data
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: file,
    }));
  
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: "", // Clear error if file is valid
    }));
  };
  

  const clearFile = (field) => {
    // Clear form state
    setFormData((prev) => ({
      ...prev,
      [field]: null,
    }));
  
    // Clear file input ref if it exists
    if (fssaiFileInputRef.current?.value) {
      fssaiFileInputRef.current.value = "";
    }
  };

  const getFinanceDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`${getFinancialDetails}/${restaurantID}`);
      if (response.success) {
        const financialDetail = response.data;
        setFormData({
          restaurantID: restaurantID,
          fssaiLicenseNo: financialDetail?.fssaiDetails?.licenseNo || "",
          fssaiIssuedOn: financialDetail?.fssaiDetails?.issuedOn
            ? formatDate(financialDetail?.fssaiDetails?.issuedOn)
            : "",
          fssaiValidUpto: financialDetail?.fssaiDetails?.validUpto
            ? formatDate(financialDetail?.fssaiDetails?.validUpto)
            : "",
          fssaiDocument: null,
          fssaiFile: financialDetail?.fssaiDetails?.file || null,
          gstRegistrationNo: financialDetail?.gstDetails?.regNo || "",
          cgst: financialDetail?.gstDetails?.cgst || "",
          sgst: financialDetail?.gstDetails?.sgst || "",
          gstIssuedOn: financialDetail?.gstDetails?.issuedOn
            ? formatDate(financialDetail?.gstDetails?.issuedOn)
            : "",
          gstDocument: null,
          gstFile: financialDetail?.gstDetails?.file || null,
          minBookingPerPerson: financialDetail?.minBookingPerPerson || "",
          // panType: financialDetail?.panDetails?.panType || "",
          panCardNo: financialDetail?.panDetails?.panNo || "",
          panDocument: null,
          panFile: financialDetail?.panDetails?.file || null,
          // bankAccountType: financialDetail?.bankDetails?.accountType || "",
          bankAccountNo: financialDetail?.bankDetails?.accountNo || "",
          bankName: financialDetail?.bankDetails?.bankName || "",
          upiId: financialDetail?.bankDetails?.upiId || "",
          bankIfscCode: financialDetail?.bankDetails?.ifscCode || "",
          bankDocument: null,
          bankFile: financialDetail?.bankDetails?.file || null,
          commission: financialDetail?.commission || "",
          partialPercentage: financialDetail?.partialPercentage || "",
          partialPerPerson: financialDetail?.partialPerPerson || "",
          landlordNOCDocument: null,
          landlordNOCFile: financialDetail?.landlordNOC?.file || null,
          electricityNOCDocument: null,
          electricityNOCFile: financialDetail?.electricityNOC?.file || null,
          fireNOCDocument: null,
          fireNOCFile: financialDetail?.fireNOC?.file || null,
          policeNOCDocument: null,
          policeNOCFile: financialDetail?.policeNOC?.file || null,
          landlordNOCIssuedOn: financialDetail?.landlordNOC?.issuedOn
            ? formatDate(financialDetail?.landlordNOC?.issuedOn)
            : "",
          landlordNOCValidTill: financialDetail?.landlordNOC?.validTill
            ? formatDate(financialDetail?.landlordNOC?.validTill)
            : "",
          landlordNOCComment: financialDetail?.landlordNOC?.comment || "",

          electricityNOCIssuedOn: financialDetail?.electricityNOC?.issuedOn
            ? formatDate(financialDetail?.electricityNOC?.issuedOn)
            : "",
          electricityNOCValidTill: financialDetail?.electricityNOC?.validTill
            ? formatDate(financialDetail?.electricityNOC?.validTill)
            : "",
          electricityNOCComment: financialDetail?.electricityNOC?.comment || "",

          fireNOCIssuedOn: financialDetail?.fireNOC?.issuedOn
            ? formatDate(financialDetail?.fireNOC?.issuedOn)
            : "",
          fireNOCValidTill: financialDetail?.fireNOC?.validTill
            ? formatDate(financialDetail?.fireNOC?.validTill)
            : "",
          fireNOCComment: financialDetail?.fireNOC?.comment || "",

          policeNOCIssuedOn: financialDetail?.policeNOC?.issuedOn
            ? formatDate(financialDetail?.policeNOC?.issuedOn)
            : "",
          policeNOCValidTill: financialDetail?.policeNOC?.validTill
            ? formatDate(financialDetail?.policeNOC?.validTill)
            : "",
          policeNOCComment: financialDetail?.policeNOC?.comment || "",
          eatingHouseLicenseDocument: null,
          eatingHouseLicenseFile:
            financialDetail?.eatingHouseLicense?.file || null,
          eatingHouseLicenseIssuedOn: financialDetail?.eatingHouseLicense
            ?.issuedOn
            ? formatDate(financialDetail?.eatingHouseLicense?.issuedOn)
            : "",
          eatingHouseLicenseValidTill: financialDetail?.eatingHouseLicense
            ?.validTill
            ? formatDate(financialDetail?.eatingHouseLicense?.validTill)
            : "",
          eatingHouseLicenseComment:
            financialDetail?.eatingHouseLicense?.comment || "",

          leaseAgreementDocument: null,
          leaseAgreementFile: financialDetail?.leaseAgreement?.file || null,
          leaseAgreementIssuedOn: financialDetail?.leaseAgreement?.issuedOn
            ? formatDate(financialDetail?.leaseAgreement?.issuedOn)
            : "",
          leaseAgreementValidTill: financialDetail?.leaseAgreement?.validTill
            ? formatDate(financialDetail?.leaseAgreement?.validTill)
            : "",
          leaseAgreementComment: financialDetail?.leaseAgreement?.comment || "",
        });
      } else {
        showToast(
          response.message || "An error occurred. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // e.preventDefault();
    try {
      setLoading(true);
      const response = await apiPut(getUpdateFinancialDetails, formData, true);
      if (response.success) {
        showToast(
          response.message || "Financial details updated successfully.",
          "success"
        );
        next(restaurantID);
      } else {
        showToast(
          response.message || "An error occurred. Please try again.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantID) {
      getFinanceDetails();
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Required fields check (non-file fields)
    const requiredFields = [
      "fssaiLicenseNo",
      "fssaiIssuedOn",
      "fssaiValidUpto",
      "gstRegistrationNo",
      "cgst",
      "sgst",
      "gstIssuedOn",
      "panCardNo",
      "bankAccountNo",
      "bankName",
      "upiId",
      "bankIfscCode",
      "commission",
      "partialPercentage",
      "partialPerPerson",
      "minBookingPerPerson",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
        isValid = false;
      }
    });

    // Document checks (either existing file or new document required)
    const documentFields = [
      { field: "pan", name: "PAN" },
      { field: "fssai", name: "FSSAI" },
      { field: "gst", name: "GST" },
      { field: "bank", name: "Bank" },
      { field: "landlordNOC", name: "Landlord NOC" },
      { field: "electricityNOC", name: "Electricity NOC" },
      { field: "fireNOC", name: "Fire Dept NOC" },
      { field: "policeNOC", name: "Police Station NOC" },
    ];

    documentFields.forEach(({ field }) => {
      const fileField = `${field}File`;
      const docField = `${field}Document`;

      if (!formData[fileField] && !formData[docField]) {
        newErrors[docField] = "This field is required";
        isValid = false;
      }
    });

    // File size validation (only for new documents)
    if (formData.panDocument?.size > 300 * 1024) {
      newErrors.panDocument = "File size must be less than 300KB";
      isValid = false;
    }
    if (formData.fssaiDocument?.size > 300 * 1024) {
      newErrors.fssaiDocument = "File size must be less than 300KB";
      isValid = false;
    }
    if (formData.gstDocument?.size > 300 * 1024) {
      newErrors.gstDocument = "File size must be less than 300KB";
      isValid = false;
    }
    if (formData.bankDocument?.size > 300 * 1024) {
      newErrors.bankDocument = "File size must be less than 300KB";
      isValid = false;
    }

    [
      "landlordNOCDocument",
      "electricityNOCDocument",
      "fireNOCDocument",
      "policeNOCDocument",
    ].forEach((field) => {
      if (formData[field]?.size > 300 * 1024) {
        newErrors[field] = "File size must be less than 300KB";
        isValid = false;
      }
    });

    // Minimum Booking Amount
    if (formData?.minBookingPerPerson && formData?.partialPerPerson) {
      if (formData?.minBookingPerPerson < 1.5 * formData?.partialPerPerson) {
        newErrors.minBookingPerPerson =
          "Minimum booking pp must be at least 1.5 times the advance booking pp.";
        isValid = false;
      }
    }

    // Field format validations
    // FSSAI License No (14 digits)
    if (formData.fssaiLicenseNo && formData.fssaiLicenseNo.length !== 14) {
      newErrors.fssaiLicenseNo = "FSSAI License Number must be 14 digits";
      isValid = false;
    }

    // GST Registration No (15 characters)
    const gstRegex = /^[0-9A-Z]{15}$/; // Better GST regex
    if (
      formData.gstRegistrationNo &&
      (formData.gstRegistrationNo.length !== 15 ||
        !gstRegex.test(formData.gstRegistrationNo))
    ) {
      newErrors.gstRegistrationNo =
        "GST registration number must be 15 characters (A-Z & 0-9 only)";
      isValid = false;
    }

    // CGST
    if (formData?.cgst) {
      if (formData.cgst <= 0 || formData.cgst >= 100) {
        newErrors.cgst = "CGST must be greater than 0 and less than 100";
        isValid = false;
      }
    }

    // SGST
    if (formData?.sgst) {
      if (formData.sgst <= 0 || formData.sgst >= 100) {
        newErrors.sgst = "SGST must be greater than 0 and less than 100";
        isValid = false;
      }
    }

    // PAN Card No (10 characters, format: ABCDE1234F)
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (formData.panCardNo && !panRegex.test(formData.panCardNo)) {
      newErrors.panCardNo =
        "PAN Number must be 10 characters (e.g., ABCDE1234F)";
      isValid = false;
    }

    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{3,64}$/;
    if (formData.upiId && !upiRegex.test(formData.upiId)) {
      newErrors.upiId = "Enter a valid UPI ID (e.g., name@bank)";
      isValid = false;
    }

    // Bank Account No (9-18 digits)
    if (formData.bankAccountNo && !/^\d{9,18}$/.test(formData.bankAccountNo)) {
      newErrors.bankAccountNo =
        "Bank Account Number must be between 9 to 18 digits";
      isValid = false;
    }

    // Bank Name
    if (formData.bankName && formData.bankName.length > 80) {
      newErrors.bankName = "Bank Name must be less than 80 characters";
      isValid = false;
    }

    // IFSC Code (11 characters, format: ABCD0123456)
    if (
      formData.bankIfscCode &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.bankIfscCode)
    ) {
      newErrors.bankIfscCode =
        "IFSC Code must be 11 characters (e.g., ABCD0123456)";
      isValid = false;
    }

    if (formData?.commission) {
      if (formData?.commission < 1 || formData?.commission > 100) {
        newErrors.commission = "Percentage must be between 1 to 100";
        isValid = false;
      }
    }

    if (formData?.partialPercentage) {
      if (
        formData?.partialPercentage < 1 ||
        formData?.partialPercentage > 100
      ) {
        newErrors.partialPercentage = "Percentage must be between 1 to 100";
        isValid = false;
      }
    }
    if (formData?.partialPerPerson) {
      if (formData?.partialPerPerson < 1) {
        newErrors.partialPerPerson =
          "Advance booking amount must be greater than 0";
        isValid = false;
      }
    }

    if (formData?.minBookingPerPerson) {
      if (formData?.minBookingPerPerson < 1) {
        newErrors.minBookingPerPerson =
          "Minimum booking amount must be greater than 0";
        isValid = false;
      }
    }

    // Date validations
    const today = dayjs();

    // FSSAI Issued On (not in future)
    if (
      formData.fssaiIssuedOn &&
      dayjs(formData.fssaiIssuedOn).isAfter(today)
    ) {
      newErrors.fssaiIssuedOn = "Issued date cannot be in the future";
      isValid = false;
    }

    // FSSAI Valid Upto (not in past and after issued date)
    if (formData.fssaiValidUpto) {
      const validUpto = dayjs(formData.fssaiValidUpto);

      if (validUpto.isBefore(today, "day")) {
        newErrors.fssaiValidUpto = "FSSAI end date cannot be in the past";
        isValid = false;
      }

      if (
        formData.fssaiIssuedOn &&
        validUpto.isBefore(dayjs(formData.fssaiIssuedOn), "day")
      ) {
        newErrors.fssaiValidUpto = "FSSAI end date must be post issued date";
        isValid = false;
      }
    }

    // GST Issued On (not in future)
    if (formData.gstIssuedOn && dayjs(formData.gstIssuedOn).isAfter(today)) {
      newErrors.gstIssuedOn = "GST issued date cannot be in the future";
      isValid = false;
    }

    const nocFields = ["landlordNOC", "electricityNOC", "fireNOC", "policeNOC"];

    nocFields.forEach((field) => {
      const issuedOn = formData[`${field}IssuedOn`];
      const validTill = formData[`${field}ValidTill`];
      const comment = formData[`${field}Comment`];

      // Issued On required
      if (!issuedOn) {
        newErrors[`${field}IssuedOn`] = "This field is required";
        isValid = false;
      } else if (dayjs(issuedOn).isAfter(today)) {
        newErrors[`${field}IssuedOn`] = "Issued date cannot be in the future";
        isValid = false;
      }

      // Valid Till required
      if (!validTill) {
        newErrors[`${field}ValidTill`] = "This field is required";
        isValid = false;
      } else {
        const validTillDate = dayjs(validTill);

        if (validTillDate.isBefore(today, "day")) {
          newErrors[`${field}ValidTill`] =
            "Valid Till date cannot be in the past";
          isValid = false;
        }

        if (issuedOn && validTillDate.isBefore(dayjs(issuedOn), "day")) {
          newErrors[`${field}ValidTill`] =
            "Valid Till date must be after Issued On date";
          isValid = false;
        }
      }

      // Comment required
      if (comment && comment.length > 100) {
        newErrors[`${field}Comment`] =
          "Comment must be less than 100 characters";
        isValid = false;
      }

      const extraDocs = ["eatingHouseLicense", "leaseAgreement"];

      extraDocs.forEach((field) => {
        const issuedOn = formData[`${field}IssuedOn`];
        const validTill = formData[`${field}ValidTill`];
        const comment = formData[`${field}Comment`];
        const file = formData[`${field}Document`];
        const viewFile = formData[`${field}File`];

        // Required: either file or existing doc
        if (!file && !viewFile) {
          newErrors[`${field}Document`] = "This field is required";
          isValid = false;
        }

        // Size Check
        if (file?.size > 300 * 1024) {
          newErrors[`${field}Document`] = "File size must be less than 300KB";
          isValid = false;
        }

        // Issued On
        if (!issuedOn) {
          newErrors[`${field}IssuedOn`] = "This field is required";
          isValid = false;
        } else if (dayjs(issuedOn).isAfter(today)) {
          newErrors[`${field}IssuedOn`] = "Issued date cannot be in the future";
          isValid = false;
        }

        // Valid Till
        if (!validTill) {
          newErrors[`${field}ValidTill`] = "This field is required";
          isValid = false;
        } else {
          const validTillDate = dayjs(validTill);

          if (validTillDate.isBefore(today, "day")) {
            newErrors[`${field}ValidTill`] =
              "Valid Till date cannot be in the past";
            isValid = false;
          }

          if (issuedOn && validTillDate.isBefore(dayjs(issuedOn), "day")) {
            newErrors[`${field}ValidTill`] =
              "Valid Till date must be after Issued On date";
            isValid = false;
          }
        }

        // Comment
        if (comment && comment.length > 100) {
          newErrors[`${field}Comment`] =
            "Comment must be less than 100 characters";
          isValid = false;
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    const stopper = setTimeout(() => {
      const isValid = validateForm();
      setIsButtonDisabled(!isValid);
    }, 300);
    return () => clearTimeout(stopper);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await requestConfirmation(
      "Want to save these details and move ahead ?"
    );
    if (!result) return;
    validateForm();
    if (restaurantID) {
      await handleSave();
    }
  };

  return (
    <Box className={styles.mainProfilePage}>
      <Helmet>
        <title>Register Restaurant | DiNEST</title>
        <meta name="description" content="Register restaurant on DiNEST" />
      </Helmet>
      <Box className={styles.mainContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Restaurant Legal & Financial Details
        </Typography>

        <form onSubmit={handleSubmit}>
          {/* FSSAI Details Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              FSSAI Details
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInput}>
                <TextField
                  label="FSSAI license no"
                  name="fssaiLicenseNo"
                  fullWidth
                  className={styles.inputTextfield}
                  type="number"
                  value={formData.fssaiLicenseNo || ""} // Ensure that the value is controlled
                  onChange={(e) => handleInputChange(e, "fssai")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors.fssaiLicenseNo && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.fssaiLicenseNo}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Issued on"
                    name="fssaiIssuedOn"
                    inputFormat="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                    fullWidth
                    value={
                      formData.fssaiIssuedOn
                        ? dayjs(formData.fssaiIssuedOn)
                        : null
                    } // Convert date to Dayjs object
                    className={styles.gridDropdownInputbox}
                    onChange={(e) => handleDateChange(e, "fssaiIssuedOn")} // Using handleDateChange
                  />
                  {errors.fssaiIssuedOn && (
                    <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                      {errors.fssaiIssuedOn}
                    </p>
                  )}
                </LocalizationProvider>
              </Grid>

              <Grid className={styles.gridInput}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Valid Till"
                    name="fssaiValidUpto"
                    inputFormat="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                    fullWidth
                    value={
                      formData.fssaiValidUpto
                        ? dayjs(formData.fssaiValidUpto)
                        : null
                    } // Convert the stored date to a Dayjs object
                    className={styles.gridDropdownInputbox}
                    onChange={(e) => handleDateChange(e, "fssaiValidUpto")} // Using handleDateChange
                  />
                  {errors.fssaiValidUpto && (
                    <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                      {errors.fssaiValidUpto}
                    </p>
                  )}
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Grid className={styles.uploadFssaiContainer}>
              <Grid container direction="column">
                <Typography variant="body1" className={styles.uploadImageTitle}>
                  {" "}
                  Upload FSSAI Document
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (PDF &lt; 300kb)
                </Typography>

                {/* File Upload Button */}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  className={styles.uploadButton}
                  onClick={() => handleClick(fssaiFileInputRef)}
                  startIcon={<CloudUploadIcon />}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    name="fssaiDocument"
                    ref={fssaiFileInputRef}
                    onChange={(e) => handleFileChange(e, "fssaiDocument")}
                    accept="application/pdf"
                  />
                </Button>

                {/* Error */}
                {errors.fssaiDocument && (
                  <p
                    className={styles.errorUpload}
                    style={{ minHeight: "1.5rem" }}
                  >
                    {errors.fssaiDocument}
                  </p>
                )}

                {/* Selected File Name + Remove Icon */}
                {formData.fssaiDocument && (
                  <Box display="flex" justifyContent="center">
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                          cursor: "pointer",
                        }}
                      >
                        {formData?.fssaiDocument?.name}
                      </Typography>
                      <IconButton onClick={() => clearFile("fssaiDocument")}>
                        <ClearIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Preview or Icon */}
                {/* <Box> */}
                {formData.fssaiFile && (
                  <IconButton
                    onClick={() => handleViewDocument(formData.fssaiFile)}
                    sx={{
                      // ml: 1,
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <Tooltip title="View FSSAI Document" arrow>
                      <Visibility sx={{ fontSize: "2rem" }} />
                    </Tooltip>
                  </IconButton>
                )}
                {/* </Box> */}
              </Grid>
            </Grid>
          </SectionPaper>

          {/* GST Details Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              GST Details
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInput}>
                <TextField
                  label="GST registration no"
                  name="gstRegistrationNo"
                  className={styles.inputTextfield}
                  type="text"
                  fullWidth
                  value={formData.gstRegistrationNo}
                  onChange={(e) => handleInputChange(e, "gst")}
                />
                {errors.gstRegistrationNo && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.gstRegistrationNo}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="CGST to diner in %"
                  name="cgst"
                  className={styles.inputTextfield}
                  fullWidth
                  type="number"
                  value={formData.cgst}
                  onChange={(e) => handleInputChange(e, "cgst")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors.cgst && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.cgst}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="SGST to diner in %"
                  name="sgst"
                  type="number"
                  className={styles.inputTextfield}
                  fullWidth
                  value={formData.sgst}
                  onChange={(e) => handleInputChange(e, "sgst")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors.sgst && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.sgst}
                  </p>
                )}
              </Grid>

              <Grid size={{ xs: 12 }} className={styles.gridInput}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Issued on"
                    name="gstIssuedOn"
                    inputFormat="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                    fullWidth
                    value={
                      formData.gstIssuedOn ? dayjs(formData.gstIssuedOn) : null
                    } // Convert date to Dayjs object
                    className={styles.gridDropdownInputbox}
                    onChange={(e) => handleDateChange(e, "gstIssuedOn")} // Using handleDateChange
                  />
                  {errors.gstIssuedOn && (
                    <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                      {errors.gstIssuedOn}
                    </p>
                  )}
                </LocalizationProvider>
              </Grid>
            </Grid>

            <Grid className={styles.uploadFssaiContainer}>
              <Grid container direction="column">
                <Typography variant="body1" className={styles.uploadImageTitle}>
                  {" "}
                  Upload GST Document
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (PDF &lt; 300kb)
                </Typography>

                {/* File Upload Button */}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  className={styles.uploadButton}
                  onClick={() => handleClick(gstFileInputRef)}
                  startIcon={<CloudUploadIcon />}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    name="gstDocument"
                    ref={gstFileInputRef}
                    onChange={(e) => handleFileChange(e, "gstDocument")}
                    accept="application/pdf"
                  />
                </Button>
                {errors.gstDocument && (
                  <p
                    className={styles.errorUpload}
                    style={{ minHeight: "1.5rem" }}
                  >
                    {errors.gstDocument}
                  </p>
                )}

                {/* Selected File Name + Remove Icon */}
                {formData.gstDocument && (
                  <Box display="flex" justifyContent="center">
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                          cursor: "pointer",
                        }}
                      >
                        {formData?.gstDocument?.name}
                      </Typography>
                      <IconButton onClick={() => clearFile("gstDocument")}>
                        <ClearIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Preview or Icon */}
                {formData.gstFile && (
                  <IconButton
                    onClick={() => handleViewDocument(formData.gstFile)}
                    sx={{
                      ml: 1,
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <Tooltip title="View GST Document" arrow>
                      <Visibility sx={{ fontSize: "2rem" }} />
                    </Tooltip>
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </SectionPaper>

          {/* PAN Card Details Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              PAN Card Details
            </Typography>

            <Grid className={styles.gridInput}>
              <TextField
                label="Pancard no"
                name="panCardNo"
                fullWidth
                className={styles.inputTextfield}
                value={formData.panCardNo}
                onChange={(e) => handleInputChange(e, "pan")}
              />
              {errors.panCardNo && (
                <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                  {errors.panCardNo}
                </p>
              )}
            </Grid>

            <Grid className={styles.uploadFssaiContainer}>
              <Grid container direction="column">
                <Typography variant="body1" className={styles.uploadImageTitle}>
                  {" "}
                  Upload PAN Document
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (PDF &lt; 300kb)
                </Typography>

                {/* File Upload Button */}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  className={styles.uploadButton}
                  onClick={handleClick(panFileInputRef)}
                  startIcon={<CloudUploadIcon />}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    name="panDocument"
                    ref={panFileInputRef}
                    onChange={(e) => handleFileChange(e, "panDocument")}
                    accept="application/pdf"
                  />
                </Button>
                {errors.panDocument && (
                  <p
                    className={styles.errorUpload}
                    style={{ minHeight: "1.5rem" }}
                  >
                    {errors.panDocument}
                  </p>
                )}

                {/* Selected File Name + Remove Icon */}
                {formData.panDocument && (
                  <Box display="flex" justifyContent="center">
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                          cursor: "pointer",
                        }}
                      >
                        {formData?.panDocument?.name}
                      </Typography>
                      <IconButton onClick={() => clearFile("panDocument")}>
                        <ClearIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Preview or Icon */}
                <Box>
                  {formData.panFile && (
                    <IconButton
                      onClick={() => handleViewDocument(formData.panFile)}
                      sx={{
                        ml: 1,
                        "&:hover": { backgroundColor: "transparent" },
                      }}
                    >
                      <Tooltip title="View PAN Document" arrow>
                        <Visibility sx={{ fontSize: "2rem" }} />
                      </Tooltip>
                    </IconButton>
                  )}
                </Box>
              </Grid>
            </Grid>
          </SectionPaper>

          {/* Bank Details Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Bank Details
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInput}>
                <TextField
                  label="Account no"
                  name="bankAccountNo"
                  fullWidth
                  type="number"
                  className={styles.inputTextfield}
                  value={formData.bankAccountNo}
                  onChange={(e) => handleInputChange(e, "bank")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors.bankAccountNo && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.bankAccountNo}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Bank name"
                  name="bankName"
                  className={styles.inputTextfield}
                  fullWidth
                  value={formData.bankName}
                  onChange={(e) => handleInputChange(e, "bank")}
                />
                {errors.bankName && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.bankName}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="IFSC code"
                  name="bankIfscCode"
                  className={styles.inputTextfield}
                  fullWidth
                  value={formData.bankIfscCode}
                  onChange={(e) => handleInputChange(e, "bank")}
                />
                {errors.bankIfscCode && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.bankIfscCode}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="UPI ID"
                  name="upiId"
                  className={styles.inputTextfield}
                  fullWidth
                  value={formData.upiId}
                  onChange={(e) => handleInputChange(e, "bank")}
                />
                {errors.upiId && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.upiId}
                  </p>
                )}
              </Grid>
            </Grid>

            <Grid className={styles.uploadFssaiContainer}>
              <Grid container direction="column">
                <Typography variant="body1" className={styles.uploadImageTitle}>
                  {" "}
                  Upload Bank Document
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (PDF &lt; 300kb)
                </Typography>
                {/* File Upload Button */}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  className={styles.uploadButton}
                  startIcon={<CloudUploadIcon />}
                  onClick={handleClick(bankFileInputRef)}
                >
                  Upload
                  <input
                    type="file"
                    name="bankDocument"
                    ref={bankFileInputRef} // FILE REF
                    hidden
                    onChange={(e) => handleFileChange(e, "bankDocument")}
                    accept="application/pdf"
                  />
                </Button>
                {errors.bankDocument && (
                  <p
                    className={styles.errorUpload}
                    style={{ minHeight: "1.5rem" }}
                  >
                    {errors.bankDocument}
                  </p>
                )}
                {/* Selected File Name + Remove Icon */}
                {formData.bankDocument && (
                  <Box display="flex" justifyContent="center">
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                          cursor: "pointer",
                        }}
                      >
                        {formData?.bankDocument?.name}
                      </Typography>
                      <IconButton onClick={() => clearFile("bankDocument")}>
                        <ClearIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {/* Preview or Icon */}
                {formData.bankFile && (
                  <IconButton
                    onClick={() => handleViewDocument(formData.bankFile)}
                    sx={{
                      ml: 1,
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  >
                    <Tooltip title="View Bank Document" arrow>
                      <Visibility sx={{ fontSize: "2rem" }} />
                    </Tooltip>
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </SectionPaper>

          {/* NOC Details Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              NOC Details
            </Typography>

            {[
              { label: "Landlord NOC", name: "landlordNOCDocument" },
              { label: "Electricity Dept NOC", name: "electricityNOCDocument" },
              { label: "Fire Dept NOC", name: "fireNOCDocument" },
              { label: "Police Station NOC", name: "policeNOCDocument" },
            ].map(({ label, name }, index) => {
              const baseName = name.replace("Document", "");

              return (
                <div key={index}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mt: 2, mb: 1 }}
                  >
                    {label}
                  </Typography>
                  {/* === Validity Dates + Comments Row === */}
                  <Grid container spacing={2}>
                    <Grid className={styles.gridInput}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Issued On"
                          inputFormat="DD/MM/YYYY"
                          format="DD/MM/YYYY"
                          className={styles.gridDropdownInputbox}
                          value={
                            formData[`${baseName}IssuedOn`]
                              ? dayjs(formData[`${baseName}IssuedOn`])
                              : null
                          }
                          onChange={(value) =>
                            handleDateChange(value, `${baseName}IssuedOn`)
                          }
                        />
                      </LocalizationProvider>

                      {errors[`${baseName}IssuedOn`] && (
                        <p
                          className={styles.error}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[`${baseName}IssuedOn`]}
                        </p>
                      )}
                    </Grid>

                    <Grid className={styles.gridInput}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Valid Till"
                          inputFormat="DD/MM/YYYY"
                          format="DD/MM/YYYY"
                          className={styles.gridDropdownInputbox}
                          value={
                            formData[`${baseName}ValidTill`]
                              ? dayjs(formData[`${baseName}ValidTill`])
                              : null
                          }
                          onChange={(value) =>
                            handleDateChange(value, `${baseName}ValidTill`)
                          }
                        />
                      </LocalizationProvider>

                      {errors[`${baseName}ValidTill`] && (
                        <p
                          className={styles.error}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[`${baseName}ValidTill`]}
                        </p>
                      )}
                    </Grid>

                    <Grid className={styles.gridInput}>
                      <TextField
                        label="Comments"
                        className={styles.inputTextfieldComments}
                        fullWidth
                        // multiline
                        // rows={5}
                        // columns={10}
                        name={`${baseName}Comment`}
                        value={formData[`${baseName}Comment`] || ""}
                        onChange={handleInputChange}
                      />
                      {errors[`${baseName}Comment`] && (
                        <p
                          className={styles.error}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[`${baseName}Comment`]}
                        </p>
                      )}
                    </Grid>
                  </Grid>

                  {/* === Upload Section === */}
                  <Grid className={styles.uploadFssaiContainer} mt={2}>
                    <Grid container direction="column">
                      <Typography
                        variant="body1"
                        className={styles.uploadImageTitle}
                      >
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (PDF &lt; 300kb)
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        className={styles.uploadButton}
                        startIcon={<CloudUploadIcon />}
                        onClick={() =>
                          nocFileInputRef.current[name]?.current?.click()
                        }
                      >
                        Upload
                        <input
                          type="file"
                          hidden
                          name={name}
                          ref={nocFileInputRef.current[name]}
                          accept="application/pdf"
                          onChange={(e) => handleFileChange(e, name)}
                        />
                      </Button>

                      {errors[name] && (
                        <p
                          className={styles.errorUpload}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[name]}
                        </p>
                      )}

                      {formData[name] && (
                        <Box display="flex" justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography
                              variant="caption"
                              sx={{
                                display: "inline-block",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                maxWidth: 120,
                                cursor: "pointer",
                              }}
                            >
                              {formData[name]?.name}
                            </Typography>
                            <IconButton onClick={() => clearFile(name)}>
                              <ClearIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}

                      {formData[`${name.replace("Document", "")}File`] && (
                        <Box mt={1}>
                          <Tooltip title={`View ${name}`} arrow>
                            <IconButton
                              onClick={() =>
                                handleViewDocument(
                                  formData[
                                    `${name.replace("Document", "")}File`
                                  ]
                                )
                              }
                              sx={{
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              <Visibility sx={{ fontSize: "2rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </div>
              );
            })}
          </SectionPaper>

          {/* Eating House License & Rent Agreement Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Eating House License & Rent Agreement
            </Typography>

            {[
              {
                label: "Eating House License",
                name: "eatingHouseLicenseDocument",
              },
              { label: "Rent Agreement", name: "leaseAgreementDocument" },
            ].map(({ label, name }, index) => {
              const baseName = name.replace("Document", "");

              return (
                <div key={index}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mt: 2, mb: 1 }}
                  >
                    {label}
                  </Typography>

                  {/* === Validity Dates + Comments Row === */}
                  <Grid container spacing={2}>
                    <Grid className={styles.gridInput}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Issued On"
                          inputFormat="DD/MM/YYYY"
                          format="DD/MM/YYYY"
                          className={styles.gridDropdownInputbox}
                          value={
                            formData[`${baseName}IssuedOn`]
                              ? dayjs(formData[`${baseName}IssuedOn`])
                              : null
                          }
                          onChange={(value) =>
                            handleDateChange(value, `${baseName}IssuedOn`)
                          }
                        />
                      </LocalizationProvider>

                      {errors[`${baseName}IssuedOn`] && (
                        <p
                          className={styles.error}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[`${baseName}IssuedOn`]}
                        </p>
                      )}
                    </Grid>

                    <Grid className={styles.gridInput}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                          label="Valid Till"
                          inputFormat="DD/MM/YYYY"
                          format="DD/MM/YYYY"
                          className={styles.gridDropdownInputbox}
                          value={
                            formData[`${baseName}ValidTill`]
                              ? dayjs(formData[`${baseName}ValidTill`])
                              : null
                          }
                          onChange={(value) =>
                            handleDateChange(value, `${baseName}ValidTill`)
                          }
                        />
                      </LocalizationProvider>

                      {errors[`${baseName}ValidTill`] && (
                        <p
                          className={styles.error}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[`${baseName}ValidTill`]}
                        </p>
                      )}
                    </Grid>

                    <Grid className={styles.gridInput}>
                      <TextField
                        label="Comments"
                        className={styles.inputTextfieldComments}
                        // fullWidth
                        // multiline
                        // rows={1}
                        name={`${baseName}Comment`}
                        value={formData[`${baseName}Comment`] || ""}
                        onChange={handleInputChange}
                      />
                      {errors[`${baseName}Comment`] && (
                        <p
                          className={styles.error}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[`${baseName}Comment`]}
                        </p>
                      )}
                    </Grid>
                  </Grid>

                  {/* === Upload Section === */}
                  <Grid className={styles.uploadFssaiContainer} mt={2}>
                    <Grid container direction="column">
                      <Typography
                        variant="body1"
                        className={styles.uploadImageTitle}
                      >
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        (PDF &lt; 300kb)
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        className={styles.uploadButton}
                        startIcon={<CloudUploadIcon />}
                        onClick={() =>
                          licenseFileInputRef.current[name]?.current?.click()
                        }
                      >
                        Upload
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          name={name}
                          ref={licenseFileInputRef.current[name]}
                          onChange={(e) => handleFileChange(e, name)}
                        />
                      </Button>

                      {errors[name] && (
                        <p
                          className={styles.errorUpload}
                          style={{ minHeight: "1.5rem" }}
                        >
                          {errors[name]}
                        </p>
                      )}

                      {formData[name] && (
                        <Box display="flex" justifyContent="center">
                          <Box display="flex" alignItems="center">
                            <Typography
                              variant="caption"
                              sx={{
                                display: "inline-block",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                maxWidth: 120,
                                cursor: "pointer",
                              }}
                            >
                              {formData[name]?.name}
                            </Typography>
                            <IconButton onClick={() => clearFile(name)}>
                              <ClearIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Box>
                        </Box>
                      )}

                      {formData[`${baseName}File`] && (
                        <Box mt={1}>
                          <Tooltip title={`View ${label}`} arrow>
                            <IconButton
                              onClick={() =>
                                handleViewDocument(formData[`${baseName}File`])
                              }
                              sx={{
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              <Visibility sx={{ fontSize: "2rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </div>
              );
            })}
          </SectionPaper>

          {/* Payments Details Section */}
          <SectionPaper className={styles.sectionColor}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Payment Details
            </Typography>

            <Grid container spacing={2}>
              <Grid className={styles.gridInput}>
                <TextField
                  label="Commission to dinest by restaurant in % "
                  name="commission"
                  type="number"
                  fullWidth
                  className={styles.inputTextfield}
                  value={formData.commission}
                  onChange={(e) => handleInputChange(e, "commission")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors?.commission && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors?.commission}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Advance booking amount in % "
                  name="partialPercentage"
                  type="number"
                  fullWidth
                  className={styles.inputTextfield}
                  value={formData.partialPercentage}
                  onChange={(e) => handleInputChange(e, "partialPercentage")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors?.partialPercentage && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors?.partialPercentage}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Advance booking amount per person in ₹ "
                  name="partialPerPerson"
                  type="number"
                  fullWidth
                  className={styles.inputTextfield}
                  value={formData.partialPerPerson}
                  onChange={(e) => handleInputChange(e, "partialPerPerson")}
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors?.partialPerPerson && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors?.partialPerPerson}
                  </p>
                )}
              </Grid>

              <Grid className={styles.gridInput}>
                <TextField
                  label="Minimum booking amount per person in ₹"
                  name="minBookingPerPerson"
                  fullWidth
                  className={styles.inputTextfield}
                  value={formData.minBookingPerPerson}
                  onChange={(e) => handleInputChange(e, "minBookingPerPerson")}
                  type="number"
                  InputProps={{
                    inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                    classes: { input: styles.noArrows },
                  }}
                />
                {errors.minBookingPerPerson && (
                  <p className={styles.error} style={{ minHeight: "1.5rem" }}>
                    {errors.minBookingPerPerson}
                  </p>
                )}
              </Grid>
            </Grid>

            <p style={{ marginTop: 10, color: "#777", fontSize: "0.8rem" }}>
              Note - Minimum booking amount pp must be always greater than
              advance booking amount pp.
            </p>
          </SectionPaper>

          {/* Navigation Buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              className={styles.prevButton}
              onClick={() => prev(restaurantID)}
              sx={{ marginRight: 2, backgroundColor: "#207d46" }}
            >
              Prev
            </Button>
            <Button
              className={styles.saveButton}
              sx={{ backgroundColor: "#207d46" }}
              disabled={isButtonDisabled}
              type="submit"
              variant="contained"
              color="primary"
            >
              Save & Next
            </Button>
          </Box>
        </form>
      </Box>
      <ViewDocument
        isOpen={showDocument}
        onClose={handleCloseDocument}
        documentUrl={documentUrl}
      />
    </Box>
  );
};

export default RestaurantDetails;
